
import React from 'react';
import { Calendar } from 'lucide-react';
import StyledButton from './ui/StyledButton';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { loadFareHarborScript, handleFareHarborBooking } from '@/utils/fareHarborUtils';

interface ReserveButtonProps {
  className?: string;
  link?: string;
  variant?: 'primary' | 'outline';
  showCalendarIcon?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  fullWidth?: boolean;
  itemId?: number; // Added itemId prop for direct booking
  flowId?: number; // Added flowId prop for direct booking
}

const ReserveButton: React.FC<ReserveButtonProps> = ({
  className,
  link = "/booking",
  variant = 'primary',
  showCalendarIcon = true,
  onClick,
  disabled = false,
  fullWidth = false,
  itemId,
  flowId = 1219724,
}) => {
  const { toast } = useToast();
  
  React.useEffect(() => {
    // Preload FareHarbor script when component mounts
    loadFareHarborScript().catch(err => 
      console.error('Error preloading FareHarbor script:', err)
    );
  }, []);
  
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // If there's a direct itemId, handle the booking through FareHarbor
    if (itemId) {
      handleFareHarborBooking({
        e,
        itemId,
        flowId,
        lightframe: true
      });
      return;
    }
    
    // If there's an onClick handler, call it
    if (onClick) {
      e.preventDefault();
      onClick();
      return;
    } 
    
    // If there's no link and no click handler, show error toast
    if (!link) {
      e.preventDefault();
      toast({
        title: "Navigation Error",
        description: "We're having trouble with this button. Please try again later.",
        variant: "destructive",
      });
      return;
    }
    
    // Track the click event for analytics
    try {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'reserve_button_click', {
          event_category: 'engagement',
          event_label: 'Reserve button clicked',
          value: 1
        });
      }
    } catch (err) {
      console.error('Analytics error:', err);
    }
  };

  return (
    <StyledButton
      href={link}
      variant={variant}
      size={variant === 'primary' ? 'lg' : 'default'}
      className={cn("font-montserrat font-medium", fullWidth && "w-full", className)}
      ariaLabel="Reserve your spot on a cat history tour"
      showCalendarIcon={showCalendarIcon}
      bookingButton={true}
      onClick={handleClick}
      disabled={disabled}
      fullWidth={fullWidth}
      data-testid="reserve-button"
    >
      <span>Reserve Your Spot</span>
    </StyledButton>
  );
};

export default ReserveButton;
